import { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import DriveDetector from '../plugins/DriveDetector';

export default function ActiveTripBanner({ onTripEnded }) {
  const [tripData, setTripData] = useState(null);
  const [stopping, setStopping] = useState(false);
  const pollRef = useRef(null);
  const listenerRef = useRef(null);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Poll for active trip status every 3 seconds
    function pollStatus() {
      DriveDetector.getDetectionStatus().then((status) => {
        if (status.state === 'tracking' || status.state === 'stopping' || status.state === 'detecting') {
          DriveDetector.getCurrentTrip().then((trip) => {
            if (trip && trip.active !== false) {
              setTripData({
                distance: trip.distance || 0,
                duration: trip.durationMinutes || 0,
                coords: trip.coordinateCount || 0,
                state: status.state,
              });
            } else {
              setTripData({ distance: 0, duration: 0, coords: 0, state: status.state });
            }
          }).catch(() => {
            setTripData({ distance: 0, duration: 0, coords: 0, state: status.state });
          });
        } else {
          setTripData(null);
        }
      }).catch(() => {});
    }

    pollStatus();
    pollRef.current = setInterval(pollStatus, 3000);

    // Listen for state changes
    const stateListener = DriveDetector.addListener('detectionStatusChanged', (data) => {
      if (data.state === 'idle') {
        setTripData(null);
        // Auto-ended trip — notify parent to refresh trip list
        if (onTripEnded) onTripEnded();
      }
    });
    listenerRef.current = stateListener;

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (listenerRef.current) {
        if (typeof listenerRef.current.remove === 'function') {
          listenerRef.current.remove();
        } else if (typeof listenerRef.current.then === 'function') {
          listenerRef.current.then((h) => h.remove());
        }
      }
    };
  }, []);

  async function handleStop() {
    setStopping(true);
    try {
      await DriveDetector.forceEndTrip();
      setTripData(null);
      if (onTripEnded) onTripEnded();
    } catch (err) {
      console.error('Failed to stop trip:', err);
    }
    setStopping(false);
  }

  if (!tripData) return null;

  const stateLabel = tripData.state === 'detecting' ? 'Detecting drive...'
    : tripData.state === 'stopping' ? 'Drive ending...'
    : 'Recording drive';

  return (
    <div className="active-trip-banner">
      <div className="active-trip-pulse" />
      <div className="active-trip-info">
        <div className="active-trip-label">{stateLabel}</div>
        <div className="active-trip-stats">
          <span>{tripData.distance.toFixed(1)} mi</span>
          <span className="active-trip-divider">&middot;</span>
          <span>{Math.floor(tripData.duration)} min</span>
        </div>
      </div>
      <button
        className="active-trip-stop"
        onClick={handleStop}
        disabled={stopping || tripData.state === 'detecting'}
      >
        {stopping ? 'Stopping...' : 'Stop'}
      </button>
    </div>
  );
}
